// ===============================================
// Purchase Order Details Page
// หน้าดูรายละเอียดใบสั่งซื้อ และรับสินค้าเข้าคลัง (Updated Version)
// ===============================================

import React, { useState, useEffect } from 'react';
import {
    Card,
    Descriptions,
    Table,
    Button,
    Space,
    Tag,
    Row,
    Col,
    Typography,
    Divider,
    message,
    Modal,
    Form,
    InputNumber,
    Input,
    Steps,
    Timeline,
    Statistic,
    Popconfirm,
    Select
} from 'antd';
import {
    ArrowLeftOutlined,
    FilePdfOutlined,
    CheckCircleOutlined,
    EditOutlined,
    DollarOutlined,
    PrinterOutlined,
    EyeOutlined,
    InboxOutlined,
    CalendarOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import {
    purchasesAPI,
    paymentsAPI,
    formatCurrency,
    formatDate,
    formatDateTime,
    handleAPIError
} from '../../services/PurchaseService';
import purchasePrintService from '../../services/purchasePrintService';
import { useAuth } from '../../contexts/AuthContext'
import dayjs from 'dayjs';
import {
    getCompanySettings,
    updateCompanySettings,
    uploadCompanyLogo,
    deleteCompanyLogo,
    getLogoUrl
} from '../../services/settingsService';


const { Title, Text } = Typography;
const { Step } = Steps;
const { TextArea } = Input;

const PurchaseOrderDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, hasPermission } = useAuth()
    const [purchase, setPurchase] = useState(null);
    const [loading, setLoading] = useState(false);
    const [receiveModalVisible, setReceiveModalVisible] = useState(false);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [receiveForm] = Form.useForm();
    const [paymentForm] = Form.useForm();

    //สำหรับ modal แสดง pdf
    const [pdfModalVisible, setPdfModalVisible] = useState(false);
    const [pdfDataUrl, setPdfDataUrl] = useState('');

    const [company, setCompany] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await getCompanySettings();
            //console.log('company data:', response.data.data);
            if (response.data.success) {
                const data = response.data.data;
                setCompany(data);
                setLogoUrl(data.logo_path ? getLogoUrl(`${data.logo_path}`) : null);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            message.error('ไม่สามารถโหลดข้อมูลบริษัทได้');
        } finally {
            setLoading(false);
            //console.log('image url:', logoUrl);
        }
    };

    // ดึงข้อมูลใบสั่งซื้อ
    const fetchPurchaseDetails = async () => {
        //console.log('ID:', id);
        setLoading(true);
        try {
            const response = await purchasesAPI.getById(id);
            console.log('detail:', response);
            if (response.success) {
                setPurchase(response.data);
            }
        } catch (error) {
            message.error(handleAPIError(error));
        } finally {
            setLoading(false);
        }
    };

    // สถานะของใบสั่งซื้อ
    const getStatusInfo = (status) => {
        const statusConfig = {
            draft: { color: 'default', text: 'ร่าง', icon: <EditOutlined /> },
            confirmed: { color: 'blue', text: 'ยืนยันแล้ว', icon: <CheckCircleOutlined /> },
            approved: { color: 'purple', text: 'อนุมัติแล้ว', icon: <CheckCircleOutlined /> },
            received: { color: 'orange', text: 'รับสินค้าแล้ว', icon: <InboxOutlined /> },
            completed: { color: 'green', text: 'เสร็จสิ้น', icon: <CheckCircleOutlined /> },
            cancelled: { color: 'red', text: 'ยกเลิก', icon: <EditOutlined /> },
        };
        return statusConfig[status] || { color: 'default', text: status, icon: <EditOutlined /> };
    };

    // ขั้นตอนการดำเนินการ
    const getOrderSteps = () => {
        const steps = [
            { title: 'สร้างใบสั่งซื้อ', status: 'finish' },
            { title: 'ยืนยันใบสั่งซื้อ', status: purchase?.status === 'draft' ? 'wait' : 'finish' },
            { title: 'รับสินค้าเข้าคลัง', status: ['received', 'completed'].includes(purchase?.status) ? 'finish' : 'wait' },
            { title: 'เสร็จสิ้น', status: purchase?.status === 'completed' ? 'finish' : 'wait' }
        ];
        return steps;
    };

    // รับสินค้าเข้าคลัง
    const handleReceiveGoods = async (values) => {
        try {
            const receiveData = {
                items: purchase.items.map(item => ({
                    purchase_id: item.purchase_id,
                    product_id: item.product_id,
                    supplier_id: purchase.supplier_id,
                    received_quantity: values[`received_${item.product_id}`] || 0,
                    unit_cost: item.unit_cost, // ใช้ราคาจากใบสั่งซื้อ
                    purchase_id: item.purchase_id,
                    product_id: item.product_id,
                    quantity: item.quantity,
                    total_cost: item.total_cost,
                    quantity_received: item.quantity_received,
                    notes: item.notes,
                    product_name: item.product_name,
                    barcode: item.barcode,
                    unit: item.unit,
                    receipt_date: item.created_at,
                })),
                supplier_id: purchase.supplier_id,
                received_by: user.id,
                notes: values.notes || '-',
                receipt_date: dayjs(),
            };

            await purchasesAPI.receiveGoods(id, receiveData);
            message.success('รับสินค้าเข้าคลังสำเร็จ');
            setReceiveModalVisible(false);
            fetchPurchaseDetails();
        } catch (error) {
            message.error(handleAPIError(error));
        }
    };

    // บันทึกการชำระเงิน
    const handlePayment = async (values) => {
        try {
            await paymentsAPI.createPayment(id, values);
            message.success('บันทึกการชำระเงินสำเร็จ');
            setPaymentModalVisible(false);
            fetchPurchaseDetails();
        } catch (error) {
            message.error(handleAPIError(error));
        }
    };

    // พิมพ์เอกสาร
    const handlePrint = async (type, action = 'download') => {
        try {

            // สร้าง PDF เป็น dataUrl
            const dataUrl = await purchasePrintService.generatePurchaseOrderPDF(company, purchase);
            setPdfDataUrl(dataUrl);
            setPdfModalVisible(true);
            message.success('สร้าง PDF เรียบร้อยแล้ว');

        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการสร้างเอกสาร');
            console.error('PDF generation error:', error);
        }
    };

    // คอลัมน์ตารางสินค้า
    const itemColumns = [
        {
            title: 'รหัสสินค้า',
            dataIndex: 'barcode',
            key: 'barcode',
            width: 120,
        },
        {
            title: 'ชื่อสินค้า',
            dataIndex: 'product_name',
            key: 'product_name',
        },
        {
            title: 'หน่วย',
            dataIndex: 'unit',
            key: 'unit',
            width: 80,
            align: 'center',
        },
        {
            title: 'จำนวน',
            dataIndex: 'quantity_ordered',
            key: 'quantity_ordered',
            width: 50,
            align: 'right',
            render: (value, record) => value ? value : record.quantity.toLocaleString('th-TH', { minimumFractionDigits: 2 }),
        },
        // {
        //     title: 'จำนวนรับ',
        //     dataIndex: 'quantity_received',
        //     key: 'quantity_received',
        //     width: 120,
        //     align: 'right',
        //     render: (value) => (
        //         <span style={{ color: value && value > 0 ? '#52c41a' : '#8c8c8c' }}>
        //             {value?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) || '0.00'}
        //         </span>
        //     ),
        // },
        {
            title: 'ราคาต่อหน่วย',
            dataIndex: 'unit_cost',
            key: 'unit_cost',
            width: 130,
            align: 'right',
            render: (value) => formatCurrency(value),
        },
        {
            title: 'ส่วนลด',
            key: 'discount',
            width: 100,
            align: 'center',
            render: (_, record) => `${record.discount_percent || 0}%`,
        },
        {
            title: 'ยอดรวม',
            dataIndex: 'line_total',
            key: 'line_total',
            width: 130,
            align: 'right',
            render: (value) => formatCurrency(value),
        },
    ];

    // คอลัมน์ตารางการชำระเงิน
    const paymentColumns = [
        {
            title: 'เลขที่ใบชำระ',
            dataIndex: 'payment_number',
            key: 'payment_number',
        },
        {
            title: 'วันที่ชำระ',
            dataIndex: 'payment_date',
            key: 'payment_date',
            render: (date) => formatDate(date),
        },
        {
            title: 'จำนวนเงิน',
            dataIndex: 'payment_amount',
            key: 'payment_amount',
            align: 'right',
            render: (amount) => formatCurrency(amount),
        },
        {
            title: 'วิธีการชำระ',
            dataIndex: 'payment_method',
            key: 'payment_method',
            render: (method) => {
                const methods = {
                    cash: 'เงินสด',
                    bank_transfer: 'โอนเงิน',
                    check: 'เช็ค',
                    other: 'อื่นๆ'
                };
                return methods[method] || method;
            },
        },
        {
            title: 'เลขที่อ้างอิง',
            dataIndex: 'reference_number',
            key: 'reference_number',
        },
    ];

    useEffect(() => {
        fetchPurchaseDetails();
    }, [id]);

    if (!purchase) {
        return <div style={{ padding: '24px', textAlign: 'center' }}>กำลังโหลด...</div>;
    }

    const statusInfo = getStatusInfo(purchase.status);

    return (
        <div style={{ padding: '24px' }}>

            {/* Modal แสดง PDF */}
            <Modal
                title="ใบสั่งซื้อสินค้า"
                open={pdfModalVisible}
                onCancel={() => setPdfModalVisible(false)}
                footer={null}
                width={'100%'}
                bodyStyle={{ height: '80vh' }}
            >
                {pdfDataUrl && (
                    <iframe
                        src={pdfDataUrl}
                        style={{ width: '100%', height: '100%' }}
                        frameBorder="0"
                        title="ตัวอย่างเอกสาร"
                    />
                )}
            </Modal>

            {/* ส่วนหัว */}
            <Card style={{ marginBottom: 24 }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate('/purchases')}
                            >
                                กลับ
                            </Button>
                            <Title level={3} style={{ margin: 0 }}>
                                ใบสั่งซื้อ {purchase.purchase_number}
                            </Title>
                            <Tag
                                color={statusInfo.color}
                                icon={statusInfo.icon}
                                style={{ fontSize: '14px', padding: '4px 8px' }}
                            >
                                {statusInfo.text}
                            </Tag>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                icon={<FilePdfOutlined />}
                                onClick={() => handlePrint('purchase_order', 'download')}
                            >
                                ดาวน์โหลดใบสั่งซื้อ
                            </Button>
                            {/* {['received', 'completed'].includes(purchase.status) && (
                                <>
                                    <Button
                                        icon={<EyeOutlined />}
                                        onClick={() => handlePrint('receipt', 'preview')}
                                        type="primary"
                                        ghost
                                    >
                                        ดูใบรับสินค้า
                                    </Button>
                                    <Button
                                        icon={<PrinterOutlined />}
                                        onClick={() => handlePrint('receipt', 'download')}
                                        type="primary"
                                    >
                                        พิมพ์ใบรับสินค้า
                                    </Button>
                                </>
                            )} */}
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* สถิติและขั้นตอน */}
            <Row gutter={24} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={8}>
                    <Card title="สถิติ" size="small">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Statistic
                                    title="ยอดรวม"
                                    value={purchase.total_amount}
                                    formatter={(value) => formatCurrency(value)}
                                    valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                                />
                            </Col>
                            {/* <Col span={12}>
                                <Statistic
                                    title="ยอดคงค้าง"
                                    value={purchase.remaining_amount || purchase.total_amount}
                                    formatter={(value) => formatCurrency(value)}
                                    valueStyle={{
                                        color: (purchase.remaining_amount || purchase.total_amount) > 0 ? '#ff4d4f' : '#52c41a',
                                        fontSize: '18px'
                                    }}
                                />
                            </Col> */}
                        </Row>
                    </Card>
                </Col>
                <Col xs={24} lg={16}>
                    <Card title="ขั้นตอนการดำเนินการ" size="small">
                        <Steps size="small" current={getOrderSteps().findIndex(step => step.status === 'wait')}>
                            {getOrderSteps().map((step, index) => (
                                <Step key={index} title={step.title} status={step.status} />
                            ))}
                        </Steps>
                    </Card>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col xs={24} lg={16}>
                    {/* ข้อมูลใบสั่งซื้อ */}
                    <Card title="รายละเอียดใบสั่งซื้อ" style={{ marginBottom: 24 }}>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="เลขที่ใบสั่งซื้อ" span={2}>
                                <Text strong>{purchase.purchase_number}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="วันที่สั่งซื้อ">
                                <CalendarOutlined style={{ marginRight: 8 }} />
                                {formatDate(purchase.purchase_date)}
                            </Descriptions.Item>
                            <Descriptions.Item label="ประเภทการชำระ">
                                <Tag color={purchase.payment_type === 'cash' ? 'green' : 'orange'}>
                                    <DollarOutlined style={{ marginRight: 4 }} />
                                    {purchase.payment_type === 'cash' ? 'เงินสด' : 'เงินเชื่อ'}
                                </Tag>
                            </Descriptions.Item>
                            {purchase.due_date && (
                                <Descriptions.Item label="วันครบกำหนด">
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {formatDate(purchase.due_date)}
                                </Descriptions.Item>
                            )}
                            {purchase.received_date && (
                                <Descriptions.Item label="วันที่รับสินค้า">
                                    <InboxOutlined style={{ marginRight: 8 }} />
                                    {formatDateTime(purchase.received_date)}
                                </Descriptions.Item>
                            )}
                            {purchase.received_by && (
                                <Descriptions.Item label="ผู้รับสินค้า">
                                    <UserOutlined style={{ marginRight: 8 }} />
                                    {purchase.received_by}
                                </Descriptions.Item>
                            )}
                            {purchase.notes && (
                                <Descriptions.Item label="หมายเหตุ" span={2}>
                                    {purchase.notes}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>

                    {/* ข้อมูลผู้จำหน่าย */}
                    <Card title="ข้อมูลผู้จำหน่าย" style={{ marginBottom: 24 }}>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="รหัสผู้จำหน่าย">
                                {purchase.supplier_id}
                            </Descriptions.Item>
                            <Descriptions.Item label="ชื่อผู้จำหน่าย">
                                {purchase.supplier_name}
                            </Descriptions.Item>
                            <Descriptions.Item label="ชื่อผู้ติดต่อ">
                                {purchase.contact_person || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="โทรศัพท์">
                                {purchase.phone || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="อีเมล">
                                {purchase.email || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="เลขประจำตัวผู้เสียภาษี">
                                {purchase.tax_id || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="ที่อยู่" span={2}>
                                {purchase.address || '-'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* รายการสินค้า */}
                    <Card title="รายการสินค้า">
                        <Table
                            columns={itemColumns}
                            dataSource={purchase.items}
                            rowKey="product_id"
                            pagination={false}
                            scroll={{ x: 800 }}
                            summary={() => (
                                <Table.Summary>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={7} align="right">
                                            <Text strong>ยอดย่อย:</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right">
                                            <Text strong>{formatCurrency(purchase.subtotal || purchase.total_amount)}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                    {purchase.discount_amount > 0 && (
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={7} align="right">
                                                <Text>ส่วนลด:</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                <Text>-{formatCurrency(purchase.discount_amount)}</Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    )}
                                    {purchase.tax_amount > 0 && (
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={7} align="right">
                                                <Text>ภาษี:</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                <Text>{formatCurrency(purchase.tax_amount)}</Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    )}
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={7} align="right">
                                            <Text strong style={{ fontSize: '16px' }}>ยอดรวมสุทธิ:</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right">
                                            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                                                {formatCurrency(purchase.total_amount)}
                                            </Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    {/* การดำเนินการ */}
                    <Card title="การดำเนินการ" style={{ marginBottom: 24 }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            {/* {['confirmed', 'approved'].includes(purchase.status) && (
                                <Button
                                    type="primary"
                                    icon={<InboxOutlined />}
                                    block
                                    onClick={() => setReceiveModalVisible(true)}
                                >
                                    รับสินค้าเข้าคลัง
                                </Button>
                            )}

                            {purchase.payment_type === 'credit' && (purchase.remaining_amount || purchase.total_amount) > 0 && (
                                <Button
                                    type="default"
                                    icon={<DollarOutlined />}
                                    block
                                    onClick={() => setPaymentModalVisible(true)}
                                >
                                    บันทึกการชำระเงิน
                                </Button>
                            )} */}

                            {purchase.status === 'draft' && (
                                <Button
                                    type="default"
                                    icon={<EditOutlined />}
                                    block
                                    onClick={() => navigate(`/purchases/edit/${id}`)}
                                >
                                    แก้ไขใบสั่งซื้อ
                                </Button>
                            )}
                        </Space>
                    </Card>

                    {/* ประวัติการชำระเงิน */}
                    {purchase.payment_type === 'credit' && purchase.payments && purchase.payments.length > 0 && (
                        <Card title="ประวัติการชำระเงิน" size="small">
                            <Table
                                columns={paymentColumns}
                                dataSource={purchase.payments}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                scroll={{ x: 400 }}
                            />
                        </Card>
                    )}
                </Col>
            </Row>

            {/* Modal รับสินค้า */}
            <Modal
                title="รับสินค้าเข้าคลัง"
                open={receiveModalVisible}
                onCancel={() => setReceiveModalVisible(false)}
                footer={null}
                width={800}
            >
                <Form
                    form={receiveForm}
                    layout="vertical"
                    onFinish={handleReceiveGoods}
                >
                    <div style={{ marginBottom: 16 }}>
                        <Title level={5}>รายการสินค้า</Title>
                        {purchase.items && purchase.items.map((item, index) => (
                            <Card size="small" key={item.product_id} style={{ marginBottom: 8 }}>
                                <Row gutter={16} align="middle">
                                    <Col span={12}>
                                        <Text strong>{item.product_name}</Text>
                                        <br />
                                        <Text type="secondary">
                                            สั่ง: {item.quantity_ordered?.toLocaleString('th-TH', { minimumFractionDigits: 2 })} {item.unit}
                                        </Text>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="จำนวนที่รับ"
                                            name={`received_${item.product_id}`}
                                            initialValue={item.quantity}
                                            style={{ margin: 0 }}
                                        >
                                            <InputNumber
                                                min={0}
                                                max={item.quantity}
                                                precision={2}
                                                style={{ width: '100%' }}
                                                addonAfter={item.unit}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                    </div>

                    <Row gutter={16}>
                        {/* <Col span={12}>
                            <Form.Item
                                label="ผู้รับสินค้า"
                                name="received_by"
                                rules={[{ required: true, message: 'กรุณากรอกชื่อผู้รับสินค้า' }]}
                            >
                                <Input placeholder="ชื่อผู้รับสินค้า" />
                            </Form.Item>
                        </Col> */}
                        <Col span={12}>
                            <Form.Item
                                label="หมายเหตุ"
                                name="notes"
                            >
                                <TextArea rows={1} placeholder="หมายเหตุเพิ่มเติม" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setReceiveModalVisible(false)}>
                                ยกเลิก
                            </Button>
                            <Button type="primary" htmlType="submit">
                                ยืนยันรับสินค้า
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            {/* Modal บันทึกการชำระเงิน */}
            <Modal
                title="บันทึกการชำระเงิน"
                open={paymentModalVisible}
                onCancel={() => setPaymentModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form
                    form={paymentForm}
                    layout="vertical"
                    onFinish={handlePayment}
                    initialValues={{
                        payment_date: new Date().toISOString().slice(0, 10),
                        payment_method: 'cash'
                    }}
                >
                    <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                        <Text strong>ยอดคงค้าง: </Text>
                        <Text style={{ fontSize: '18px', color: '#ff4d4f' }}>
                            {formatCurrency(purchase.remaining_amount || purchase.total_amount)}
                        </Text>
                    </div>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="วันที่ชำระ"
                                name="payment_date"
                                rules={[{ required: true, message: 'กรุณาเลือกวันที่ชำระ' }]}
                            >
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="จำนวนเงิน"
                                name="payment_amount"
                                rules={[
                                    { required: true, message: 'กรุณากรอกจำนวนเงิน' },
                                    {
                                        validator: (_, value) => {
                                            const remainingAmount = purchase.remaining_amount || purchase.total_amount;
                                            if (value > remainingAmount) {
                                                return Promise.reject('จำนวนเงินเกินยอดคงค้าง');
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                            >
                                <InputNumber
                                    min={0}
                                    max={purchase.remaining_amount || purchase.total_amount}
                                    precision={2}
                                    style={{ width: '100%' }}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="วิธีการชำระ"
                                name="payment_method"
                                rules={[{ required: true, message: 'กรุณาเลือกวิธีการชำระ' }]}
                            >
                                <Select>
                                    <Select.Option value="cash">เงินสด</Select.Option>
                                    <Select.Option value="bank_transfer">โอนเงิน</Select.Option>
                                    <Select.Option value="check">เช็ค</Select.Option>
                                    <Select.Option value="other">อื่นๆ</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="เลขที่อ้างอิง"
                                name="reference_number"
                            >
                                <Input placeholder="เลขที่เช็ค, เลขที่โอน ฯลฯ" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="หมายเหตุ"
                        name="notes"
                    >
                        <TextArea rows={3} placeholder="หมายเหตุเพิ่มเติม" />
                    </Form.Item>

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setPaymentModalVisible(false)}>
                                ยกเลิก
                            </Button>
                            <Button type="primary" htmlType="submit">
                                บันทึกการชำระเงิน
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default PurchaseOrderDetails;